import React, { useEffect } from 'react';
import { useNestedStateMachine } from '../hooks/useNestedStateMachine';
import { Alert, Card, List, Typography, Button, Space, Spin } from 'antd';
import { PlayCircleOutlined, CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

interface NestedStateMachineViewerProps {
  processId: string;
  onStateChange?: (newState: string) => void;
}

export const NestedStateMachineViewer: React.FC<NestedStateMachineViewerProps> = ({
  processId,
  onStateChange,
}) => {
  const {
    nestedStateMachine,
    validationErrors,
    isLoading,
    error,
    executionResult,
    loadNestedStateMachine,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    validateNestedStateMachine,
    executeNestedStateMachine,
  } = useNestedStateMachine(processId);

  useEffect(() => {
    void loadNestedStateMachine();
  }, [loadNestedStateMachine]);

  const handleExecute = async (stateId: string) => {
    const result = await executeNestedStateMachine(stateId);
    if (result && onStateChange) {
      onStateChange(result.next_state);
    }
  };

  if (isLoading) {
    return <Spin size="large" />;
  }

  if (error) {
    return <Alert type="error" message={error} />;
  }

  if (!nestedStateMachine) {
    return <Alert type="info" message="No nested state machine found" />;
  }

  return (
    <Card>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Title level={4}>{nestedStateMachine.artifact.name}</Title>
        <Text type="secondary">{nestedStateMachine.artifact.description}</Text>

        {validationErrors.length > 0 && (
          <Alert
            type="warning"
            message="Validation Errors"
            description={
              <List
                size="small"
                dataSource={validationErrors}
                renderItem={(error) => (
                  <List.Item>
                    <WarningOutlined style={{ color: '#faad14' }} /> {error}
                  </List.Item>
                )}
              />
            }
          />
        )}

        <List
          header={<Title level={5}>States</Title>}
          dataSource={nestedStateMachine.states}
          renderItem={(state) => (
            <List.Item
              actions={[
                <Button
                  key="execute"
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  onClick={() => handleExecute(state.id)}
                  disabled={!state.is_initial && !executionResult}
                >
                  Execute
                </Button>,
              ]}
            >
              <List.Item.Meta
                avatar={
                  state.is_initial ? (
                    <PlayCircleOutlined style={{ color: '#52c41a' }} />
                  ) : state.is_final ? (
                    <CheckCircleOutlined style={{ color: '#1890ff' }} />
                  ) : null
                }
                title={state.name}
                description={state.description}
              />
            </List.Item>
          )}
        />

        <List
          header={<Title level={5}>Transitions</Title>}
          dataSource={nestedStateMachine.transitions}
          renderItem={(transition) => (
            <List.Item>
              <List.Item.Meta
                title={transition.name}
                description={`From: ${
                  nestedStateMachine.states.find((s) => s.id === transition.from_state)?.name
                } → To: ${nestedStateMachine.states.find((s) => s.id === transition.to_state)?.name}`}
              />
            </List.Item>
          )}
        />
      </Space>
    </Card>
  );
};
